import type { ReactNode } from 'react';

import { Iconify } from '@nexus/ari-lib';

import i18n from '../lib/i18n';

// Feature flags
export const ENABLE_AUTO_DETECT = false; // No auto-detect in ari-cloud
export const AUTO_DETECT_READY = false; // Set to true when bot detection is implemented

export const AUTO_DETECT_LANGUAGE = 'auto-detect';

// Use standard ISO 639-1 language codes
export type LanguageCode =
  | 'en'
  | 'fr'
  | 'de'
  | 'it'
  | 'es'
  | 'pt'
  | 'zh'
  | 'pl'
  | 'ja'
  | 'tr'
  | 'nl'
  | typeof AUTO_DETECT_LANGUAGE;

export interface Language {
  flag: string | ReactNode;
  language: LanguageCode;
}

const AUTO_DETECT_OPTION: Language = {
  flag: <Iconify icon="ri:voice-ai-fill" style={{ width: 30, marginRight: -7, marginLeft: -5 }} />,
  language: AUTO_DETECT_LANGUAGE,
};

const LANGUAGES: readonly Language[] = [
  { flag: '🇺🇸', language: 'en' },
  { flag: '🇫🇷', language: 'fr' },
  { flag: '🇩🇪', language: 'de' },
  { flag: '🇮🇹', language: 'it' },
  { flag: '🇪🇸', language: 'es' },
] as const;

export const SUPPORTED_LANGUAGES: readonly Language[] = ENABLE_AUTO_DETECT
  ? [AUTO_DETECT_OPTION, ...LANGUAGES]
  : LANGUAGES;

const DEFAULT_LANGUAGE: Language = { flag: '🇺🇸', language: 'en' };

export const getLanguageName = (language: string) => {
  if (language === AUTO_DETECT_LANGUAGE) {
    return 'Auto Detect';
  }

  const languageKeys: Record<string, string> = {
    en: 'languages.en',
    fr: 'languages.fr',
    de: 'languages.de',
    it: 'languages.it',
    es: 'languages.es',
    pt: 'languages.pt',
    nl: 'languages.nl',
    zh: 'languages.zh',
    pl: 'languages.pl',
    ja: 'languages.ja',
    tr: 'languages.tr',
  };
  return i18n.t(languageKeys[language] || 'languages.en');
};

export function getLanguageFromI18(language: string | undefined): Language {
  // Handle undefined or empty language (i18n not initialized yet)
  if (!language) {
    return DEFAULT_LANGUAGE;
  }

  // Normalize browser language codes to base codes (en-US -> en)
  const normalized = language.split('-')[0].toLowerCase();

  // Find exact match
  const exactMatch = SUPPORTED_LANGUAGES.find((lang) => lang.language === normalized);

  if (exactMatch) return exactMatch;

  // Default to English if no match found
  return DEFAULT_LANGUAGE;
}
